'use client'

import { useGoogleAuth } from '@components/hooks/useGoogleAuth/index.client'
import useLocalStorageState from '@components/hooks/useLocalStorageState'
import verifyGoogleFedCMToken from '@lib/api/auth/verifyGoogleFedCMToken'
import { EMAIL_FROM_FEDCM_LOCAL_STORAGE_KEY } from '@lib/browsers/localStorage/LocalStorageItem'
import { consoleLogger } from '@lib/logger'
import PatientRoutes from '@lib/routes/PatientRoutes'
import { trackEvent } from '@lib/tracking'
import getSegmentAnonymousIdFromClientCookie from '@lib/tracking/getSegmentAnonymousIdFromClientCookie'
import { TrackingEvents } from '@lib/tracking/TrackingEvents'
import { TrackingSources } from '@lib/tracking/TrackingSources'
import { useEffect } from 'react'

export default function FedCMWrapper({
  children,
  fedcmClientId,
  oauthClientId,
  oauthRedirectUrl,
  slug,
}: {
  children: React.ReactNode
  fedcmClientId: string
  oauthClientId: string
  oauthRedirectUrl: string
  slug: string
}) {
  const [emailFromLocalStorage, setEmailFromLocalStorage] =
    useLocalStorageState<string | undefined>(
      EMAIL_FROM_FEDCM_LOCAL_STORAGE_KEY,
      undefined,
    )
  const { token } = useGoogleAuth({
    disableFedCM: Boolean(emailFromLocalStorage),
    googleFedcmClientId: fedcmClientId,
    googleOauthClientId: oauthClientId,
    googleOauthRedirectUri: oauthRedirectUrl,
  })

  useEffect(() => {
    if (emailFromLocalStorage) {
      return
    }

    if (token) {
      verifyGoogleFedCMToken({
        redirectPath: PatientRoutes.trialApplyBeginAuto(slug),
        segmentAnonymousId: getSegmentAnonymousIdFromClientCookie(),
        token,
      }).then((response) => {
        if (response.success && response.data) {
          const { email: emailFromFedCM } = response.data
          if (emailFromFedCM) {
            consoleLogger.info('Email from FedCM: %o', emailFromFedCM)
            trackEvent(TrackingEvents.TRIAL_APPLICATION_EMAIL_OAUTH_PREFILLED, {
              source: TrackingSources.GOOGLE_FEDCM,
              val: emailFromFedCM,
            })
            setEmailFromLocalStorage(emailFromFedCM)
          }
        }
      })
    }
  }, [emailFromLocalStorage, token])

  return children
}
